import { Button, type ButtonProps } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { TrackLocations } from "@/hooks/use-segment";
import {
    type CheckScmAuthForProjectResult,
    getRepoUrlFromInitializer,
    type PlainRunnerEnvironmentClass,
} from "@/queries/environment-queries";
import { useListProjectEnvironmentClasses, type PlainProject } from "@/queries/project-queries";
import type { PlainAuthenticationMethodPersonalAccessToken } from "@/queries/runner-queries";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError, isCreditsExhaustionError, isPolicyError, isVerifyCardError } from "@/utils/errors";
import { type FC, forwardRef, memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SelectEnvironmentClassForProjectModal } from "@/components/projects/SelectEnvironmentClassForProjectModal";
import { type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useGetPreferredEnvironmentClassPerProject } from "@/hooks/use-preferred-env-class-per-project";
import { useCreateEnvironmentFromProjectWithAuth } from "@/hooks/use-create-environment-from-project";
import { Code, ConnectError } from "@connectrpc/connect";
import { CreateEnvironmentFromProjectBadRequestEnvironmentClassDetails } from "gitpod-next-api/gitpod/v1/environment_pb";

type Props = {
    project?: PlainProject;
    onCreateSuccess?: () => void;
    hideTooltip?: boolean;
    showProjectEnvClassSelector?: boolean;
} & ButtonProps;

export const CreateEnvironmentButton = memo(
    forwardRef<HTMLButtonElement, Props>(
        ({ project, onCreateSuccess, hideTooltip, showProjectEnvClassSelector, ...props }, ref) => {
            const { toast } = useToast();
            const navigate = useNavigate();

            const { preferredEnvironmentClassPerProject, isPending: isPendingPreference } =
                useGetPreferredEnvironmentClassPerProject();
            const { data: projectEnvironmentClasses, isLoading: isLoadingProjectClasses } =
                useListProjectEnvironmentClasses(project?.id);

            const [showScmAuthModal, setShowScmAuthModal] = useState<
                (CheckScmAuthForProjectResult & { rememberChoice?: boolean }) | undefined
            >();
            const [showEnvClassSelectorModal, setShowEnvClassSelectorModal] = useState<
                { filteredEnvironmentClassIds?: string[]; title?: string; description?: string } | undefined
            >();

            const { handleCreateEnvironmentFromProject: createEnvironmentFromProjectWithAuth, isLoading: isCreating } =
                useCreateEnvironmentFromProjectWithAuth();

            const _showProjectEnvClassSelector = useMemo(() => {
                return (
                    showProjectEnvClassSelector ??
                    (!!project?.id &&
                        !preferredEnvironmentClassPerProject?.get(project.id) &&
                        projectEnvironmentClasses?.environmentClasses &&
                        projectEnvironmentClasses.environmentClasses.length > 1)
                );
            }, [preferredEnvironmentClassPerProject, project, projectEnvironmentClasses, showProjectEnvClassSelector]);

            const createEnvironment = useCallback(
                async (
                    project: PlainProject,
                    preferredEnvClassId?: string,
                    rememberChoice?: boolean,
                    skipAuthCheck?: boolean,
                ) => {
                    try {
                        const result = await createEnvironmentFromProjectWithAuth({
                            project,
                            projectEnvironmentClasses: projectEnvironmentClasses?.environmentClasses ?? [],
                            preferredEnvClassId,
                            rememberChoice,
                            skipAuthCheck,
                        });

                        if (result?.type === "success") {
                            toast({ title: "Created new environment" });
                            onCreateSuccess?.();
                            navigate(getDetailsURL({ environment: result.environment }));
                            setShowScmAuthModal(undefined);
                        } else if (result?.type === "authRequired") {
                            setShowScmAuthModal({ ...result.authResult, rememberChoice });
                        }
                    } catch (e) {
                        if (
                            !_showProjectEnvClassSelector &&
                            e instanceof ConnectError &&
                            (e.code === Code.FailedPrecondition || e.code === Code.NotFound) &&
                            !isCreditsExhaustionError(e) && // These are handled by the top level credits exhaustion check
                            !isVerifyCardError(e) && // These are handled by the top level verify card check
                            !isPolicyError(e) // Policy errors are terminal — don't show env class selector
                        ) {
                            const details = e.findDetails(
                                CreateEnvironmentFromProjectBadRequestEnvironmentClassDetails,
                            );
                            setShowEnvClassSelectorModal({
                                filteredEnvironmentClassIds:
                                    details.length > 0 && details[0].environmentClassIds.length > 0
                                        ? details[0].environmentClassIds
                                        : undefined,
                                title: "Failed to create environment",
                                description: `${formatError(e)}`,
                            });
                        } else if (
                            !isVerifyCardError(e) && // These are handled by the top level verify card check
                            !isCreditsExhaustionError(e) // These are handled by the top level credits exhaustion check
                        ) {
                            toast({
                                title: "Failed to create an environment",
                                description: formatError(e),
                                type: "error",
                                duration: 10_000,
                            });
                        }
                    }
                },
                [
                    createEnvironmentFromProjectWithAuth,
                    projectEnvironmentClasses?.environmentClasses,
                    toast,
                    onCreateSuccess,
                    navigate,
                    _showProjectEnvClassSelector,
                ],
            );

            const disabled = props.disabled || !project || isPendingPreference || isLoadingProjectClasses;

            return (
                <>
                    <Tooltip content={hideTooltip ? "" : "Create environment"} usePortal>
                        <Button
                            {...props}
                            ref={ref}
                            type="button"
                            disabled={disabled}
                            loading={isCreating}
                            onClick={async () => {
                                if (project) {
                                    if (_showProjectEnvClassSelector) {
                                        setShowEnvClassSelectorModal({});
                                    } else {
                                        await createEnvironment(
                                            project,
                                            preferredEnvironmentClassPerProject.get(project.id),
                                        );
                                    }
                                }
                            }}
                            aria-label={props["aria-label"] ?? "Create environment"}
                            data-testid={`create-environment-from-project-button-${project?.id}`}
                            data-tracking-id="create-environment-button"
                        />
                    </Tooltip>
                    {showEnvClassSelectorModal && project && (
                        <SelectEnvironmentClassForProjectModal
                            project={project}
                            filteredEnvironmentClassIds={showEnvClassSelectorModal.filteredEnvironmentClassIds}
                            title={showEnvClassSelectorModal.title}
                            description={showEnvClassSelectorModal.description}
                            onClose={() => setShowEnvClassSelectorModal(undefined)}
                            onSelect={async (environmentClass: EnvironmentClassEntry, rememberChoice: boolean) => {
                                setShowEnvClassSelectorModal(undefined);
                                await createEnvironment(project, environmentClass.clazz.id, rememberChoice);
                            }}
                        />
                    )}
                    {showScmAuthModal && (
                        <AuthenticationModal
                            authenticationUrl={showScmAuthModal.authenticationResult.authenticationUrl}
                            patAuth={showScmAuthModal.authenticationResult.supportsPat}
                            environmentClass={showScmAuthModal.environmentClass}
                            scmId={showScmAuthModal.authenticationResult.scmId}
                            scmName={showScmAuthModal.authenticationResult.scmName}
                            repoURL={getRepoUrlFromInitializer(showScmAuthModal.project.initializer) || ""}
                            onClose={() => setShowScmAuthModal(undefined)}
                            onAuthSuccess={() =>
                                createEnvironment(
                                    showScmAuthModal.project,
                                    showScmAuthModal.environmentClass.id,
                                    showScmAuthModal.rememberChoice,
                                    true,
                                )
                            }
                        />
                    )}
                </>
            );
        },
    ),
);
CreateEnvironmentButton.displayName = "CreateEnvironmentButton";

type SCMAuthenticationModalProps = {
    authenticationUrl: string;
    patAuth: PlainAuthenticationMethodPersonalAccessToken | undefined;
    scmId: string;
    scmName: string;
    environmentClass: PlainRunnerEnvironmentClass;
    repoURL: string;
    onClose: () => void;
    onAuthSuccess: () => void;
};
const AuthenticationModal: FC<SCMAuthenticationModalProps> = memo((p) => {
    return (
        <div>
            <SCMAuthenticationModal
                repoURL={p.repoURL}
                authenticationUrl={p.authenticationUrl}
                patAuth={p.patAuth}
                scmId={p.scmId}
                scmName={p.scmName}
                onClose={p.onClose}
                onClickContinue={p.onAuthSuccess}
                runnerId={p.environmentClass.runnerId}
                data-track-location={TrackLocations.CreateEnvironmentSCMAuthenticationModal}
            />
        </div>
    );
});
AuthenticationModal.displayName = "AuthenticationModal";
